/**
 * AXERP (Frappe) CRM client — access-request submissions.
 *
 * WHY THIS IS NOT AN IFRAME
 * ─────────────────────────
 * The obvious integration is the one Frappe hands you:
 *
 *   <iframe src="https://erp.tspgusa.com/websites-contact-inquiry" ...>
 *
 * That cannot work from this origin. `erp.tspgusa.com` responds with
 * `X-Frame-Options: SAMEORIGIN` on every route including the web-form page, so
 * the iframe renders blank in every browser. Verified with a HEAD request, not
 * assumed. So we post the same payload to the same public endpoint over AJAX
 * and own the markup — which is also what lets the form carry CareBee branding
 * and consumer-facing labels instead of Frappe's default chrome.
 *
 * durteq.com already uses this exact pattern against this exact web form; this
 * is a deliberate reuse, not a new invention.
 *
 * FIELD CONTRACT (read before adding a field)
 * ───────────────────────────────────────────
 * The `contact-inquiry` web form is backed by the `CRM Lead` doctype and is
 * anonymous (`login_required: 0`, `anonymous: 1`). Its fields, verified live:
 *
 *   first_name       Data        REQUIRED
 *   last_name        Data        optional
 *   email            Data/Email  REQUIRED
 *   mobile_no        Data/Phone  REQUIRED   ← required by the form, so the UI
 *                                            must require it too or Frappe
 *                                            rejects the submission
 *   organization     Data        optional
 *   job_title        Data        optional
 *   inquiry_subject  Select      optional   ← SEE THE WARNING BELOW
 *   inquiry_message  Small Text  REQUIRED
 *   source_site      Data        hidden
 *   source           Data        hidden, defaults to "Website"
 *
 * ⚠ `inquiry_subject` IS A SHARED SELECT WITH DEFENCE/MARITIME OPTIONS.
 * Its allowed values are Government/Defence procurement, Coast Guard/Maritime
 * agency, Emergency services/SAR, Prime contractor/Integrator, Commercial fleet
 * operator, Sovereign/Government partner, Commercial/Offtake buyer, Investor,
 * Media, Other. None of them describe a family caregiver. Sending
 * "Family caregiver" fails with ValidationError — tested. Until a CareBee
 * option set is added in AXERP, we OMIT the field entirely (verified: omitting
 * it is accepted) and put the caregiver's role in `inquiry_message` instead, so
 * the lead still arrives with that context and nothing is silently dropped.
 *
 * ⚠ `source` IS A LINK FIELD, NOT FREE TEXT.
 * It resolves against `CRM Lead Source` — only existing records validate, and
 * anything else fails with LinkValidationError. A `carebeeapp` record was
 * created in AXERP on 2026-08-05 for this site (alongside the existing
 * `durteq_com`). If it is ever renamed or deleted, EVERY submission from this
 * site fails until the constant below matches a real record again.
 * `source_site` is plain Data, so it accepts any string.
 */

const ENDPOINT =
  'https://erp.tspgusa.com/api/method/frappe.website.doctype.web_form.web_form.accept';
const WEB_FORM = 'contact-inquiry';

/** Link → CRM Lead Source. Only existing records validate. See header note. */
const LEAD_SOURCE = 'carebeeapp';
/** Plain Data field — safe for arbitrary attribution. */
const SOURCE_SITE = 'carebeeapp';

const GENERIC_ERROR =
  'Sorry — your request could not be sent. Please check your details and try again in a moment.';

/** The caregiver context we collect, since inquiry_subject cannot express it. */
export type CaregiverRole =
  | 'Caring for a parent'
  | 'Caring for a spouse or partner'
  | 'Caring for a child or dependent'
  | 'Caring for more than one person'
  | 'Planning ahead / not yet caregiving'
  | 'Clinician, employer or partner';

export interface AccessRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: CaregiverRole;
  platform: string;
  message: string;
}

export type SubmitResult =
  | { ok: true; leadId: string }
  | { ok: false; error: string };

/**
 * Frappe wraps user-facing validation errors in a doubly-encoded
 * `_server_messages` envelope: a JSON string containing an array of JSON
 * strings. Pull the first message out and strip its HTML so we can show the
 * real reason ("Email is not valid") rather than a generic failure.
 */
function serverMessage(payload: unknown): string {
  try {
    const raw = (payload as { _server_messages?: string })._server_messages;
    if (!raw) return '';
    const messages = JSON.parse(raw) as string[];
    const first = JSON.parse(messages[0]) as { message?: string };
    return (first.message ?? '').replace(/<[^>]*>/g, '').trim();
  } catch {
    return '';
  }
}

/**
 * Compose the free-text body. The caregiver's role and platform preference go
 * here because the CRM has nowhere structured to put them yet — see the
 * inquiry_subject warning above. Losing them would make the lead much less
 * useful to whoever picks it up.
 */
function composeMessage(req: AccessRequest): string {
  const lines = [
    'CareBee app access request (carebeeapp.com)',
    '',
    `Caregiving situation: ${req.role}`,
    `Preferred platform: ${req.platform}`,
  ];
  if (req.message.trim()) {
    lines.push('', 'What they told us:', req.message.trim());
  }
  return lines.join('\n');
}

export async function submitAccessRequest(
  req: AccessRequest,
): Promise<SubmitResult> {
  const fields: Record<string, string> = {
    first_name: req.firstName.trim(),
    last_name: req.lastName.trim(),
    email: req.email.trim(),
    mobile_no: req.phone.trim(),
    inquiry_message: composeMessage(req),
    source: LEAD_SOURCE,
    source_site: SOURCE_SITE,
    // inquiry_subject deliberately omitted — see header note.
  };

  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // Frappe expects `data` as a JSON *string*, not a nested object. Passing
      // an object yields a TypeError from the server.
      body: JSON.stringify({ web_form: WEB_FORM, data: JSON.stringify(fields) }),
    });

    const body: unknown = await res.json().catch(() => ({}));

    if (res.ok && (body as { message?: { name?: string } }).message?.name) {
      return {
        ok: true,
        leadId: (body as { message: { name: string } }).message.name,
      };
    }
    return { ok: false, error: serverMessage(body) || GENERIC_ERROR };
  } catch {
    // Network failure, DNS, offline, CORS. Nothing actionable to show.
    return { ok: false, error: GENERIC_ERROR };
  }
}
